import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DatePicker, Button, Spin, message, Table, Tooltip, Select, Input, Modal, Badge, Checkbox, Popover } from 'antd';
import { DownloadOutlined, ReloadOutlined, InfoCircleOutlined, SaveOutlined, ExclamationCircleOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { fetchActivitiesByLocation, fetchPlanVersions, updateVarianceReason } from './api';
import { REASON_OPTIONS } from './constants/reasonCategories';
import { getCleaningPlannedDuration, getCleaningPlannedMinutes } from './constants/cleaningPlannedHours';

const { RangePicker } = DatePicker;

// CSS Styles matching the BA dashboard design
const styles = {
  container: {
    padding: '12px 16px',
    fontFamily: "'DM Sans', -apple-system, sans-serif",
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
  },
  header: {
    background: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    padding: '12px 16px',
    marginBottom: '12px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a202c',
    letterSpacing: '-0.5px',
    margin: 0,
  },
  headerMeta: {
    fontSize: '12px',
    color: '#4a5568',
  },
  tabNav: {
    display: 'flex',
    gap: '2px',
    marginBottom: '10px',
    background: '#ffffff',
    padding: '4px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    width: 'fit-content',
    border: '1px solid #e2e8f0',
  },
  tabBtn: {
    padding: '7px 18px',
    border: 'none',
    background: 'transparent',
    color: '#4a5568',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
  },
  tabBtnActive: {
    background: '#2c5282',
    color: 'white',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
  },
  filters: {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '8px 12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    marginBottom: '10px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'nowrap',
    border: '1px solid #e2e8f0',
    overflowX: 'auto',
  },
  filterLabel: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  filterDivider: {
    width: '1px',
    height: '24px',
    background: '#e2e8f0',
    flexShrink: 0,
  },
  slicers: {
    display: 'flex',
    alignItems: 'center',
    background: '#f7fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    overflow: 'hidden',
    flexShrink: 0,
  },
  slicerItem: {
    padding: '5px 12px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#4a5568',
    cursor: 'pointer',
    transition: 'all 0.2s',
    borderRight: '1px solid #e2e8f0',
    whiteSpace: 'nowrap',
  },
  slicerItemActive: {
    background: '#3182ce',
    color: 'white',
  },
  datetimeGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
  },
  dateField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  fieldLabel: {
    fontSize: '9px',
    fontWeight: '700',
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  timeSlicers: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    flexShrink: 0,
  },
  timeBtn: {
    padding: '5px 10px',
    border: '1px solid #e2e8f0',
    background: '#ffffff',
    color: '#4a5568',
    borderRadius: '5px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  timeBtnActive: {
    background: '#3182ce',
    color: 'white',
    borderColor: '#3182ce',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '10px',
    marginBottom: '10px',
  },
  kpiCard: {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '10px 12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e2e8f0',
    transition: 'all 0.2s ease',
    position: 'relative',
    overflow: 'hidden',
  },
  kpiCardAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #3182ce, #2c5282)',
    opacity: 0,
    transition: 'opacity 0.3s',
  },
  kpiHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '6px',
  },
  kpiTitle: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  kpiValue: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1a202c',
    lineHeight: 1,
    marginBottom: '3px',
    fontFamily: "'JetBrains Mono', monospace",
  },
  kpiSubtitle: {
    fontSize: '11px',
    color: '#4a5568',
    fontWeight: '500',
  },
  kpiSplit: {
    display: 'flex',
    gap: '10px',
    marginTop: '6px',
    paddingTop: '6px',
    borderTop: '1px solid #e2e8f0',
  },
  splitItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
  },
  splitDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
  },
  tableContainer: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
  },
  tableHeader: {
    padding: '10px 14px',
    borderBottom: '1px solid #e2e8f0',
    background: '#f7fafc',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#1a202c',
    margin: 0,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    fontFamily: "'JetBrains Mono', monospace",
  },
  badgeSuccess: {
    background: '#c6f6d5',
    color: '#22543d',
  },
  badgeDanger: {
    background: '#fed7d7',
    color: '#742a2a',
  },
  badgeNeutral: {
    background: '#e2e8f0',
    color: '#4a5568',
  },
  btnExport: {
    padding: '6px 12px',
    background: '#2c5282',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  remarksInput: {
    width: '160px',
  },
  multiSelectWrapper: {
    position: 'relative',
    display: 'inline-block',
  },
  multiSelectTrigger: {
    padding: '4px 26px 4px 8px',
    border: '1px solid #e2e8f0',
    borderRadius: '5px',
    background: '#ffffff',
    fontSize: '11px',
    fontWeight: '500',
    cursor: 'pointer',
    minWidth: '140px',
    maxWidth: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'all 0.2s',
    position: 'relative',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  multiSelectDropdown: {
    position: 'absolute',
    top: 'calc(100% + 3px)',
    left: 0,
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '7px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.08)',
    zIndex: 1000,
    minWidth: '240px',
    maxHeight: '360px',
    overflow: 'hidden',
  },
};

// Equipment groups for pivot table display
const EQUIPMENT_GROUPS = ['Granulation', 'FBP', 'Blending', 'Compression', 'Coating', 'Capsulation', 'Imprinting', 'Packing', 'Other'];

// ─── Reason field helpers ─────────────────────────────────────────────────────

/** Parse reasonCatagory DB string → { reason: string[], reasonDurations: Record<string, number|null> }
 *  Supports new JSON format (with optional __unaccounted__ entry) and legacy comma-string. */
const parseReasonField = (raw) => {
  if (!raw) return { reason: [], reasonDurations: {} };
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object' && parsed[0] !== null && 'reason' in parsed[0]) {
      const reason = parsed.map(item => item.reason).filter(r => r && r !== '__unaccounted__');
      const reasonDurations = {};
      parsed.forEach(item => {
        if (item.reason && item.reason !== '__unaccounted__')
          reasonDurations[item.reason] = item.duration != null ? Number(item.duration) : null;
      });
      return { reason, reasonDurations };
    }
  } catch (e) { /* not JSON — fall through to comma-split */ }
  const reason = raw.split(',').map(r => r.trim()).filter(Boolean);
  const reasonDurations = {};
  reason.forEach(r => { reasonDurations[r] = null; });
  return { reason, reasonDurations };
};

/** Serialize reasons + optional per-reason durations back to DB string.
 *  Appends __unaccounted__ entry when provided. Falls back to comma-string when no durations set. */
const serializeReasonField = (reasons, reasonDurations, unaccountedHrs) => {
  if (!Array.isArray(reasons) || reasons.length === 0) return '';
  const durations = reasonDurations || {};
  const hasAnyDuration = reasons.some(r => durations[r] != null);
  const hasUnaccounted = unaccountedHrs != null && unaccountedHrs > 0.001;
  if (hasAnyDuration || hasUnaccounted) {
    const arr = reasons.map(r => ({ reason: r, duration: durations[r] != null ? durations[r] : null }));
    if (hasUnaccounted) arr.push({ reason: '__unaccounted__', duration: unaccountedHrs });
    return JSON.stringify(arr);
  }
  return reasons.join(', ');
};

/** Parse variance string (+H:MM / -H:MM) → signed minutes. */
const _parseVarianceToMinutes = (variance) => {
  if (!variance || variance === '-' || variance === 'running') return 0;
  const match = variance.match(/^([+-])?(\d+):(\d+)$/);
  if (!match) return 0;
  return (match[1] === '-' ? -1 : 1) * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
};

/** Returns { reason → minutes } for a row.
 *  Uses explicit per-reason durations when provided; remaining reasons share leftover time equally.
 *  Falls back to equal-split when no durations are set (preserves existing behaviour). */
const getMinutesPerReasonMap = (row) => {
  const reasons = Array.isArray(row.reason) ? row.reason : (row.reason ? [row.reason] : []);
  if (reasons.length === 0) return {};
  const cycleVarMinutes = Math.abs(_parseVarianceToMinutes(row.cycleVariation));
  if (cycleVarMinutes <= 0) return {};
  const durations = row.reasonDurations || {};
  const hasAnyExplicit = reasons.some(r => durations[r] != null);
  const result = {};
  if (hasAnyExplicit) {
    // Only include reasons with explicit durations; blanks are excluded (surface as Unaccounted)
    reasons.forEach(r => { if (durations[r] != null) result[r] = durations[r] * 60; });
  } else {
    // Legacy rows with no durations at all → equal split across all reasons
    const share = cycleVarMinutes / reasons.length;
    reasons.forEach(r => { result[r] = share; });
  }
  return result;
};

/** Returns minutes of cycle variance not covered by explicit reason durations.
 *  Returns 0 for legacy rows with no durations, or when not all reasons are filled. */
const getUnaccountedMinutes = (row) => {
  const reasons = Array.isArray(row.reason) ? row.reason : (row.reason ? [row.reason] : []);
  if (reasons.length === 0) return 0;
  const cycleVarMinutes = Math.abs(_parseVarianceToMinutes(row.cycleVariation));
  if (cycleVarMinutes <= 0) return 0;
  const durations = row.reasonDurations || {};
  const allFilled = reasons.length > 0 && reasons.every(r => durations[r] != null);
  if (!allFilled) return 0;
  const explicitMins = reasons.reduce((s, r) => s + durations[r] * 60, 0);
  return Math.max(0, cycleVarMinutes - explicitMins);
};

/** Plain-text HH:MM duration input. Stores decimal hours in parent; displays/accepts HH:MM. */
const DurationInput = ({ value, onChange, hasError }) => {
  const toDisplay = (v) => {
    if (v == null || v === '') return '';
    const h = Math.floor(v);
    const m = Math.round((v - h) * 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };
  const [text, setText] = React.useState(() => toDisplay(value));
  React.useEffect(() => { setText(toDisplay(value)); }, [value]);

  const commit = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) { onChange(null); setText(''); return; }
    const match = trimmed.match(/^(\d{1,3}):([0-5]\d)$/);
    if (match) {
      const decimal = parseInt(match[1], 10) + parseInt(match[2], 10) / 60;
      onChange(decimal);
      setText(toDisplay(decimal));
    } else {
      // invalid — reset to last committed value
      setText(toDisplay(value));
    }
  };

  return (
    <Input
      size="small"
      placeholder="HH:MM"
      value={text}
      maxLength={6}
      onChange={(e) => setText(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onPressEnter={(e) => commit(e.target.value)}
      style={{
        width: '80px',
        fontSize: '11px',
        fontFamily: "'JetBrains Mono', monospace",
        ...(hasError ? { borderColor: '#ff4d4f' } : {})
      }}
      status={hasError ? 'error' : undefined}
    />
  );
};

const ProductionVarianceDashboard = () => {
  // State
  const [activeTab, setActiveTab] = useState('variance'); // 'variance' or 'reasons'
  const [operationFilter, setOperationFilter] = useState('all'); // 'all', 'batch', 'cleaning'
  const [activeTimeRange, setActiveTimeRange] = useState('1W');
  
  // Calculate default date/time range dynamically
  const calculateDefaultRange = (range = '1W') => {
    const now = new Date();
    const rounded = new Date(now);
    if (rounded.getMinutes() > 0 || rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0) {
      rounded.setHours(rounded.getHours() + 1);
    }
    rounded.setMinutes(0, 0, 0);
    const end = dayjs(rounded);
    
    let start;
    switch (range) {
      case '1D': start = end.subtract(1, 'day'); break;
      case '1W': start = end.subtract(7, 'day'); break;
      case '1M': start = end.subtract(1, 'month'); break;
      case '3M': start = end.subtract(3, 'month'); break;
      case '6M': start = end.subtract(6, 'month'); break;
      default: start = end.subtract(7, 'day');
    }
    return [start, end];
  };

  const [dateTimeRange, setDateTimeRange] = useState(() => calculateDefaultRange('1W'));
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [rawApiData, setRawApiData] = useState(null);
  
  // Plan version state
  const [planVersions, setPlanVersions] = useState([]);
  const [selectedPlanVersion, setSelectedPlanVersion] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Modified rows tracking for save functionality
  const [modifiedRows, setModifiedRows] = useState(new Set()); // Set of row keys that have been modified
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  // Expandable columns state
  const [planColumnsExpanded, setPlanColumnsExpanded] = useState(false);
  const [actualColumnsExpanded, setActualColumnsExpanded] = useState(false);

  // Equipment filter state (for tracking filtered groups in groupedTableData)
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [reasonsEquipmentFilter, setReasonsEquipmentFilter] = useState([]);
  const [reasonsEquipmentNumbers, setReasonsEquipmentNumbers] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [expandedDurationRows, setExpandedDurationRows] = useState(new Set());

  // Helper functions
  const formatDateTime = (dateStr) => {
    if (!dateStr || dateStr === 'running') return dateStr || '-';
    const date = dayjs(dateStr);
    return date.isValid() ? date.format('DD-MM HH:mm') : '-';
  };

  const formatDuration = (hours) => {
    if (hours === null || hours === undefined) return '-';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const calculateDuration = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return '-';
    if (endDateStr === 'running') return 'running';
    const start = dayjs(startDateStr);
    const end = dayjs(endDateStr);
    if (!start.isValid() || !end.isValid()) return '-';
    const diffMs = end.diff(start);
    if (diffMs < 0) return '-';
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const calculateDurationHours = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return null;
    if (endDateStr === 'running') return null;
    const start = dayjs(startDateStr);
    const end = dayjs(endDateStr);
    if (!start.isValid() || !end.isValid()) return null;
    const diffMs = end.diff(start);
    if (diffMs < 0) return null;
    return diffMs / (1000 * 60 * 60);
  };

  const getAreaFromEquipmentName = (equipmentName) => {
    if (!equipmentName) return 'Other';
    const name = String(equipmentName).toUpperCase();
    if (name.startsWith('COMP-') || name.startsWith('COMP')) return 'Compression';
    if (name.startsWith('COAT-') || name.startsWith('COAT')) return 'Coating';
    if (name.startsWith('CAPS-') || name.startsWith('CAPS')) return 'Capsulation';
    if (name.startsWith('IMP-') || name.startsWith('IMP')) return 'Imprinting';
    if (name.startsWith('FBP-') || name.startsWith('FBP')) return 'FBP';
    if (/^P\d+\s*(PRIMARY|SECONDARY)$/.test(name)) return 'Packing';
    if (name.match(/^B\d/)) return 'Blending';
    if (name.match(/^G\d/)) return 'Granulation';
    return 'Other';
  };

  const isProductionActivity = (activity) => {
    const activityType = (activity.activity_type || '').toLowerCase();
    if (activityType === 'cleaning' || activityType === 'maintenance') return false;
    const hasBatchNumber = activity.plan_product || activity.actual_product;
    const isProduction = activityType === 'manufacturing' || 
                        activityType === 'operation' ||
                        activityType === 'production' ||
                        (activity.plan_activity || '').toUpperCase() === 'PRODUCTION';
    return hasBatchNumber || isProduction;
  };

  const isCleaningActivity = (activity) => {
    const activityType = (activity.activity_type || '').toLowerCase();
    const planActivity = (activity.plan_activity || '').toLowerCase();
    return activityType === 'cleaning' || planActivity.includes('cleaning') || planActivity.includes('clean');
  };

  const getCleaningType = (activity) => {
    const planActivity = activity.plan_activity || '';
    const actualActivity = activity.actual_activity || '';
    const typeMatch = (planActivity + ' ' + actualActivity).match(/type[-\s]?([a-z])/i);
    if (typeMatch) return `Type-${typeMatch[1].toUpperCase()}`;
    if (planActivity.toLowerCase().includes('major') || actualActivity.toLowerCase().includes('major')) return 'Major';
    if (planActivity.toLowerCase().includes('minor') || actualActivity.toLowerCase().includes('minor')) return 'Minor';
    return planActivity || actualActivity || 'Cleaning';
  };

  // Calculate Plan Variation (Actual Start - Planned Start)
  const calculatePlanVariation = (planStart, actualStart) => {
    if (!planStart || !actualStart) return { value: '-', ms: null };
    const planStartDate = dayjs(planStart);
    const actualStartDate = dayjs(actualStart);
    if (!planStartDate.isValid() || !actualStartDate.isValid()) return { value: '-', ms: null };
    const varianceMs = actualStartDate.diff(planStartDate);
    const isNegative = varianceMs < 0;
    const absVarianceMs = Math.abs(varianceMs);
    const totalMinutes = Math.floor(absVarianceMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const formatted = `${hours}:${minutes.toString().padStart(2, '0')}`;
    return { value: isNegative ? `-${formatted}` : `+${formatted}`, ms: varianceMs };
  };

  // Calculate Cycle Variation (Actual Duration - Planned Duration)
  const calculateCycleVariation = (planStart, planEnd, actualStart, actualEnd) => {
    if (!planStart || !planEnd || !actualStart || !actualEnd) return { value: '-', ms: null };
    if (actualEnd === 'running') return { value: 'running', ms: null };
    const planStartDate = dayjs(planStart);
    const planEndDate = dayjs(planEnd);
    const actualStartDate = dayjs(actualStart);
    const actualEndDate = dayjs(actualEnd);
    if (!planStartDate.isValid() || !planEndDate.isValid() || 
        !actualStartDate.isValid() || !actualEndDate.isValid()) return { value: '-', ms: null };
    const plannedMs = planEndDate.diff(planStartDate);
    const actualMs = actualEndDate.diff(actualStartDate);
    if (plannedMs < 0 || actualMs < 0) return { value: '-', ms: null };
    const varianceMs = actualMs - plannedMs;
    const isNegative = varianceMs < 0;
    const absVarianceMs = Math.abs(varianceMs);
    const totalMinutes = Math.floor(absVarianceMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const formatted = `${hours}:${minutes.toString().padStart(2, '0')}`;
    return { value: isNegative ? `-${formatted}` : `+${formatted}`, ms: varianceMs };
  };

  // Calculate Cycle Variation for cleaning using constant planned hours
  const calculateCleaningCycleVariation = (plannedMinutes, actualStart, actualEnd) => {
    if (!plannedMinutes || !actualStart || !actualEnd) return { value: '-', ms: null };
    if (actualEnd === 'running') return { value: 'running', ms: null };
    const actualStartDate = dayjs(actualStart);
    const actualEndDate = dayjs(actualEnd);
    if (!actualStartDate.isValid() || !actualEndDate.isValid()) return { value: '-', ms: null };
    const actualMs = actualEndDate.diff(actualStartDate);
    if (actualMs < 0) return { value: '-', ms: null };
    const plannedMs = plannedMinutes * 60 * 1000; // Convert minutes to ms
    const varianceMs = actualMs - plannedMs;
    const isNegative = varianceMs < 0;
    const absVarianceMs = Math.abs(varianceMs);
    const totalMinutes = Math.floor(absVarianceMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const formatted = `${hours}:${minutes.toString().padStart(2, '0')}`;
    return { value: isNegative ? `-${formatted}` : `+${formatted}`, ms: varianceMs };
  };

  // Check if activity is completed (has actual_end_time and not running)
  const isActivityCompleted = (activity) => {
    return activity.actual_end_time && activity.actual_end_time !== 'running';
  };

  // Check if activity has actual equipment info (actual_equipment_id and actual_equipment_location)
  const hasActualEquipmentInfo = (activity) => {
    return activity.actual_equipment_id && activity.actual_equipment_location;
  };

  // Process API data into combined table rows
  const processData = useCallback((apiData) => {
    const rows = [];
    let rowKey = 0;
    if (!apiData?.data) return rows;

    Object.entries(apiData.data).forEach(([equipmentName, equipmentData]) => {
      const area = getAreaFromEquipmentName(equipmentName);
      const eqpSequence = equipmentData.eqp_sequence ?? 9999;
      const allActivities = [
        ...(equipmentData.matched || []),
        ...(equipmentData.not_started || []),
        ...(equipmentData.unplanned || [])
      ];

      // Process Production (Batch) activities - only completed ones with actual equipment info
      const productionActivities = allActivities.filter(a => isProductionActivity(a) && isActivityCompleted(a) && hasActualEquipmentInfo(a));
      productionActivities.forEach((activity) => {
        const batchNumber = activity.plan_product || activity.actual_product || '-';
        const batchesMatch = activity.plan_product && activity.actual_product && 
                           activity.plan_product.toLowerCase() === activity.actual_product.toLowerCase();
        
        const planVariationResult = batchesMatch 
          ? calculatePlanVariation(activity.plan_start_date, activity.actual_start_time)
          : { value: '-', ms: null };
        
        const cycleVariationResult = batchesMatch 
          ? calculateCycleVariation(
              activity.plan_start_date, activity.plan_end_date, 
              activity.actual_start_time, activity.actual_end_time
            )
          : { value: '-', ms: null };
        
        rows.push({
          key: rowKey++,
          type: 'batch',
          typeIcon: '⚙️',
          area,
          equipmentName,
          planEquipmentLocation: activity.plan_equipment_location || '-',
          actualEquipmentLocation: activity.actual_equipment_location || '-',
          eqpSequence: activity.eqp_sequence ?? eqpSequence,
          batchOrId: batchNumber,
          planDuration: calculateDuration(activity.plan_start_date, activity.plan_end_date),
          planStart: formatDateTime(activity.plan_start_date),
          planEnd: formatDateTime(activity.plan_end_date),
          actualDuration: calculateDuration(activity.actual_start_time, activity.actual_end_time),
          actualStart: formatDateTime(activity.actual_start_time),
          actualEnd: formatDateTime(activity.actual_end_time),
          planVariation: planVariationResult.value,
          planVarianceMs: planVariationResult.ms,
          cycleVariation: cycleVariationResult.value,
          cycleVarianceMs: cycleVariationResult.ms,
          ...parseReasonField(activity.reasonCatagory),
          remarks: activity.remarks || '',
          planId: activity.plan_id || null, // ID for API update - only use plan_id, not activity.id
          status: activity.status,
          hasData: !!(activity.plan_start_date && activity.actual_start_time),
          rawPlanStart: activity.plan_start_date,
          rawActualStart: activity.actual_start_time,
          rawActualEnd: activity.actual_end_time,
        });
      });

      // Process Cleaning activities - only completed ones with actual equipment info
      const cleaningActivities = allActivities.filter(a => isCleaningActivity(a) && isActivityCompleted(a) && hasActualEquipmentInfo(a));
      cleaningActivities.forEach((activity) => {
        const cleaningType = getCleaningType(activity);
        const planVariationResult = calculatePlanVariation(activity.plan_start_date, activity.actual_start_time);
        
        // For Type-A and Type-C cleaning, use constant planned hours
        const constantPlannedMinutes = getCleaningPlannedMinutes(area, cleaningType);
        const constantPlannedDuration = getCleaningPlannedDuration(area, cleaningType);
        
        // Use constant-based cycle variation for Type-A/Type-C, otherwise use date-based
        const cycleVariationResult = constantPlannedMinutes
          ? calculateCleaningCycleVariation(constantPlannedMinutes, activity.actual_start_time, activity.actual_end_time)
          : calculateCycleVariation(
              activity.plan_start_date, activity.plan_end_date, 
              activity.actual_start_time, activity.actual_end_time
            );
        
        // Use constant planned duration for Type-A/Type-C, otherwise calculate from dates
        const planDuration = constantPlannedDuration || calculateDuration(activity.plan_start_date, activity.plan_end_date);
        
        rows.push({
          key: rowKey++,
          type: 'cleaning',
          typeIcon: '🧹',
          area,
          equipmentName,
          planEquipmentLocation: activity.plan_equipment_location || '-',
          actualEquipmentLocation: activity.actual_equipment_location || '-',
          eqpSequence: activity.eqp_sequence ?? eqpSequence,
          batchOrId: cleaningType,
          planDuration: planDuration,
          planStart: formatDateTime(activity.plan_start_date),
          planEnd: formatDateTime(activity.plan_end_date),
          actualDuration: calculateDuration(activity.actual_start_time, activity.actual_end_time),
          actualStart: formatDateTime(activity.actual_start_time),
          actualEnd: formatDateTime(activity.actual_end_time),
          planVariation: planVariationResult.value,
          planVarianceMs: planVariationResult.ms,
          cycleVariation: cycleVariationResult.value,
          cycleVarianceMs: cycleVariationResult.ms,
          ...parseReasonField(activity.reasonCatagory),
          remarks: activity.remarks || '',
          planId: activity.plan_id || null, // ID for API update - only use plan_id, not activity.id
          status: activity.status,
          hasData: !!(activity.plan_start_date && activity.actual_start_time),
          rawPlanStart: activity.plan_start_date,
          rawActualStart: activity.actual_start_time,
          rawActualEnd: activity.actual_end_time,
        });
      });
    });

    // Sort by equipment sequence, then by actual start
    rows.sort((a, b) => {
      if (a.eqpSequence !== b.eqpSequence) return a.eqpSequence - b.eqpSequence;
      if (a.rawActualStart && b.rawActualStart) return new Date(a.rawActualStart) - new Date(b.rawActualStart);
      return 0;
    });
    return rows;
  }, []);

  // Filter and group table data
  const filteredTableData = useMemo(() => {
    // Apply operation filter
    let data = operationFilter === 'all' ? tableData : tableData.filter(row => row.type === operationFilter);
    
    // Apply group filter if any groups are filtered
    if (filteredGroups.length > 0) {
      data = data.filter(row => filteredGroups.includes(row.area));
    }
    
    return data;
  }, [tableData, operationFilter, filteredGroups]);

  // Group table data by equipment category for display (just filtered data, no group headers)
  const groupedTableData = useMemo(() => {
    return filteredTableData;
  }, [filteredTableData]);

  // Get available equipment options for Reasons Summary filter (dynamic based on data)
  const availableEquipmentOptions = useMemo(() => {
    // Get unique equipment from filtered table data
    const equipmentMap = {};
    filteredTableData.forEach(row => {
      if (row.equipmentName && row.area) {
        if (!equipmentMap[row.equipmentName]) {
          equipmentMap[row.equipmentName] = row.area;
        }
      }
    });
    
    // Convert to options array sorted by area then equipment name
    return Object.entries(equipmentMap)
      .map(([name, area]) => ({ label: name, value: name, area }))
      .sort((a, b) => {
        if (a.area !== b.area) return a.area.localeCompare(b.area);
        return a.label.localeCompare(b.label);
      });
  }, [filteredTableData]);

  // Auto-select equipment when equipment group filter changes
  useEffect(() => {
    if (reasonsEquipmentFilter.length > 0) {
      // Select all equipment in the selected groups
      const equipmentInGroups = availableEquipmentOptions
        .filter(eq => reasonsEquipmentFilter.includes(eq.area))
        .map(eq => eq.value);
      setReasonsEquipmentNumbers(equipmentInGroups);
    } else {
      // Clear equipment selection when no groups selected
      setReasonsEquipmentNumbers([]);
    }
  }, [reasonsEquipmentFilter, availableEquipmentOptions]);

  // Flash a brief loader in the Reasons Summary tab on client-side filter changes.
  // dateTimeRange is intentionally excluded — the main `loading` state covers API fetches.
  useEffect(() => {
    if (activeTab !== 'reasons') return;
    setReasonsLoading(true);
    const t = setTimeout(() => setReasonsLoading(false), 350);
    return () => clearTimeout(t);
  }, [reasonsEquipmentFilter, reasonsEquipmentNumbers, operationFilter, activeTab]);

  // Calculate KPI statistics
  const kpiStats = useMemo(() => {
    const data = filteredTableData;
    
    // Total records
    const totalRecords = data.length;
    
    // Matching records (have both plan and actual data)
    const matchingRecords = data.filter(r => r.hasData);
    const matchingCount = matchingRecords.length;
    
    // Variance noted (has plan or cycle variance that is not '-')
    const varianceRecords = matchingRecords.filter(r => 
      (r.planVariation !== '-' && r.planVariation !== '+0:00') || 
      (r.cycleVariation !== '-' && r.cycleVariation !== '+0:00' && r.cycleVariation !== 'running')
    );
    const varianceNoted = varianceRecords.length;
    
    // Delayed vs Early
    const delayedRecords = matchingRecords.filter(r => 
      (r.planVarianceMs !== null && r.planVarianceMs > 0) ||
      (r.cycleVarianceMs !== null && r.cycleVarianceMs > 0)
    );
    const delayed = delayedRecords.length;
    
    const earlyRecords = matchingRecords.filter(r => 
      (r.planVarianceMs !== null && r.planVarianceMs < 0) ||
      (r.cycleVarianceMs !== null && r.cycleVarianceMs < 0)
    );
    const early = earlyRecords.length;
    
    // Reasons filled (reason is now an array)
    const reasonsFilledRecords = varianceRecords.filter(r => Array.isArray(r.reason) ? r.reason.length > 0 : !!r.reason);
    const reasonsFilled = reasonsFilledRecords.length;
    const reasonsPct = varianceNoted > 0 ? Math.round((reasonsFilled / varianceNoted) * 100) : 0;
    
    // Batch vs Cleaning counts
    const batchCount = data.filter(r => r.type === 'batch').length;
    const cleaningCount = data.filter(r => r.type === 'cleaning').length;

    return {
      totalRecords,
      matchingCount,
      varianceNoted,
      delayed,
      early,
      reasonsFilled,
      reasonsPct,
      batchCount,
      cleaningCount
    };
  }, [filteredTableData]);

  // Fetch data from API
  const fetchData = useCallback(async () => {
    if (!dateTimeRange || dateTimeRange.length !== 2) {
      message.warning('Please select a date range');
      return;
    }
    setLoading(true);
    try {
      const [start, end] = dateTimeRange;
      const startDate = start.format('YYYY-MM-DD HH:mm:ss');
      const endDate = end.format('YYYY-MM-DD HH:mm:ss');
      
      const apiParams = {
        start_date: startDate,
        end_date: endDate
      };
      if (selectedPlanVersion !== null) {
        apiParams.plan_version = selectedPlanVersion;
      }
      
      const response = await fetchActivitiesByLocation(apiParams);
      if (response?.success) {
        setRawApiData(response);
        const processed = processData(response);
        setTableData(processed);
      } else {
        message.error('Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Error fetching data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [dateTimeRange, selectedPlanVersion, processData]);

  // Export to Excel
  const handleExportExcel = async () => {
    if (!dateTimeRange || dateTimeRange.length !== 2) {
      message.warning('Please select a date range');
      return;
    }
    setExporting(true);
    try {
      const [start, end] = dateTimeRange;
      const startDate = start.format('YYYY-MM-DD HH:mm:ss');
      const endDate = end.format('YYYY-MM-DD HH:mm:ss');
      const headers = ['Type', 'Equipment', 'Plan Loc', 'Actual Loc', 'Batch/ID', 'Plan Duration', 'Plan Start', 'Plan End', 
                       'Actual Duration', 'Actual Start', 'Actual End', 'Plan Variance', 'Cycle Variance', 'Reason', 'Reason Durations', 'Remarks'];
      const fmtMins = (m) => { const h = Math.floor(m / 60); const mn = Math.round(m % 60); return `${h}:${mn.toString().padStart(2, '0')}`; };
      const csvRows = [
        headers.join(','),
        ...filteredTableData.map(row => {
          const reasons = Array.isArray(row.reason) ? row.reason : (row.reason ? [row.reason] : []);
          const durations = row.reasonDurations || {};
          let durationStr = '';
          if (reasons.length > 0 && reasons.some(r => durations[r] != null)) {
            const parts = reasons
              .filter(r => durations[r] != null)
              .map(r => `${r}: ${fmtMins(durations[r] * 60)}`);
            const uMins = getUnaccountedMinutes(row);
            if (uMins > 0.5) parts.push(`Unaccounted: ${fmtMins(uMins)}`);
            durationStr = parts.join(' | ');
          }
          return [
            row.type === 'batch' ? 'Batch' : 'Cleaning',
            row.equipmentName,
            row.planEquipmentLocation || '-',
            row.actualEquipmentLocation || '-',
            row.batchOrId,
            row.planDuration,
            row.planStart,
            row.planEnd,
            row.actualDuration,
            row.actualStart,
            row.actualEnd,
            row.planVariation,
            row.cycleVariation,
            reasons.join(', '),
            durationStr,
            row.remarks || ''
          ].map(cell => `"${cell}"`).join(',');
        })
      ];
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `production_variance_${startDate.split(' ')[0]}_to_${endDate.split(' ')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Export completed successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      message.error('Error exporting data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  // Export Reason Category Summary to Excel (2-sheet format)
  const handleExportReasonsSummary = () => {
    if (!dateTimeRange || dateTimeRange.length !== 2) {
      message.warning('Please select a date range');
      return;
    }
    
    try {
      const [start, end] = dateTimeRange;
      const startDateTime = start.format('DD-MM-YYYY HH:mm');
      const endDateTime = end.format('DD-MM-YYYY HH:mm');
      const startDateFile = start.format('YYYY-MM-DD');
      const endDateFile = end.format('YYYY-MM-DD');
      
      // Helper to parse variance to minutes
      const parseVarianceToMinutes = (variance) => {
        if (!variance || variance === '-' || variance === 'running') return 0;
        const match = variance.match(/^([+-])?(\d+):(\d+)$/);
        if (!match) return 0;
        const sign = match[1] === '-' ? -1 : 1;
        const hours = parseInt(match[2], 10);
        const minutes = parseInt(match[3], 10);
        return sign * (hours * 60 + minutes);
      };

      // Helper to format minutes as HH:MM
      const formatMinutesToHHMM = (totalMinutes) => {
        const hours = Math.floor(totalMinutes / 60);
        const mins = Math.round(totalMinutes % 60);
        return `${hours}:${mins.toString().padStart(2, '0')}`;
      };

      // Filter data same as table
      const dataForReasons = reasonsEquipmentNumbers.length > 0
        ? filteredTableData.filter(row => reasonsEquipmentNumbers.includes(row.equipmentName))
        : (reasonsEquipmentFilter.length > 0
          ? filteredTableData.filter(row => reasonsEquipmentFilter.includes(row.area))
          : filteredTableData);

      // Build pivot data structure for Sheet 1
      const UNACCOUNTED_KEY = 'Unaccounted';
      const pivotData = {};
      [...REASON_OPTIONS, UNACCOUNTED_KEY].forEach(r => {
        pivotData[r] = { byGroup: {} };
        EQUIPMENT_GROUPS.forEach(g => pivotData[r].byGroup[g] = 0);
      });
      
      // Equipment-wise detail data for Sheet 2
      const equipmentDetails = []; // Array of { equipment, equipmentGroup, reason, minutes }
      
      dataForReasons.forEach(row => {
        const minuteMap = getMinutesPerReasonMap(row);
        const reasons = Object.keys(minuteMap);
        const equipmentGroup = row.area || 'Other';
        const equipmentName = row.equipmentName || 'Unknown';
        const activityDate = row.rawActualStart ? dayjs(row.rawActualStart).format('DD-MM-YYYY') : '-';
        const remarks = row.remarks || '';
        if (reasons.length > 0) {
          reasons.forEach(reason => {
            if (pivotData[reason]) {
              pivotData[reason].byGroup[equipmentGroup] = (pivotData[reason].byGroup[equipmentGroup] || 0) + minuteMap[reason];
              equipmentDetails.push({
                equipment: equipmentName,
                equipmentGroup: equipmentGroup,
                date: activityDate,
                reason: reason,
                minutes: minuteMap[reason],
                remarks: remarks
              });
            }
          });
        }
        const uMins = getUnaccountedMinutes(row);
        if (uMins > 0.5) {
          pivotData[UNACCOUNTED_KEY].byGroup[equipmentGroup] = (pivotData[UNACCOUNTED_KEY].byGroup[equipmentGroup] || 0) + uMins;
          equipmentDetails.push({
            equipment: equipmentName,
            equipmentGroup: equipmentGroup,
            date: activityDate,
            reason: UNACCOUNTED_KEY,
            minutes: uMins,
            remarks: remarks
          });
        }
      });

      // Calculate column totals
      const columnTotals = {};
      EQUIPMENT_GROUPS.forEach(g => columnTotals[g] = 0);
      
      // Build pivot rows (Sheet 1): reason rows sorted by total, Unaccounted appended at end
      const pivotRows = REASON_OPTIONS
        .filter(reason => {
          const total = Object.values(pivotData[reason].byGroup).reduce((sum, m) => sum + m, 0);
          return total > 0;
        })
        .map(reason => {
          const row = { reason };
          EQUIPMENT_GROUPS.forEach(g => {
            row[g] = pivotData[reason].byGroup[g];
            columnTotals[g] += pivotData[reason].byGroup[g];
          });
          row.total = Object.values(pivotData[reason].byGroup).reduce((sum, m) => sum + m, 0);
          return row;
        })
        .sort((a, b) => b.total - a.total);

      const uTotal = Object.values(pivotData[UNACCOUNTED_KEY].byGroup).reduce((sum, m) => sum + m, 0);
      if (uTotal > 0.5) {
        const uRow = { reason: UNACCOUNTED_KEY };
        EQUIPMENT_GROUPS.forEach(g => {
          uRow[g] = pivotData[UNACCOUNTED_KEY].byGroup[g];
          columnTotals[g] += pivotData[UNACCOUNTED_KEY].byGroup[g];
        });
        uRow.total = uTotal;
        pivotRows.push(uRow);
      }

      const grandTotal = Object.values(columnTotals).reduce((sum, m) => sum + m, 0);

      // ===== Sheet 1: Pivot Summary =====
      const sheet1Data = [
        ['Reason Category Summary - Pivot View'],
        ['From:', startDateTime],
        ['To:', endDateTime],
        ['Equipment Groups:', reasonsEquipmentFilter.length > 0 ? reasonsEquipmentFilter.join(', ') : 'All'],
        ['Equipment IDs:', reasonsEquipmentNumbers.length > 0 ? reasonsEquipmentNumbers.join(', ') : 'All'],
        [], // Empty row
        ['Reason Category', ...EQUIPMENT_GROUPS, 'Total'],
        ...pivotRows.map(row => [
          row.reason,
          ...EQUIPMENT_GROUPS.map(g => row[g] > 0 ? formatMinutesToHHMM(row[g]) : '-'),
          formatMinutesToHHMM(row.total)
        ]),
        // Total row
        ['TOTAL', ...EQUIPMENT_GROUPS.map(g => columnTotals[g] > 0 ? formatMinutesToHHMM(columnTotals[g]) : '-'), formatMinutesToHHMM(grandTotal)]
      ];

      // ===== Sheet 2: Equipment-wise Details (Individual records, not aggregated) =====
      const detailRows = equipmentDetails
        .sort((a, b) => {
          // Sort by equipment group, then equipment name, then date, then reason
          if (a.equipmentGroup !== b.equipmentGroup) return a.equipmentGroup.localeCompare(b.equipmentGroup);
          if (a.equipment !== b.equipment) return a.equipment.localeCompare(b.equipment);
          if (a.date !== b.date) return a.date.localeCompare(b.date);
          return a.reason.localeCompare(b.reason);
        });

      const sheet2Data = [
        ['Equipment-wise Reason Details'],
        ['From:', startDateTime],
        ['To:', endDateTime],
        ['Equipment Groups:', reasonsEquipmentFilter.length > 0 ? reasonsEquipmentFilter.join(', ') : 'All'],
        ['Equipment IDs:', reasonsEquipmentNumbers.length > 0 ? reasonsEquipmentNumbers.join(', ') : 'All'],
        [], // Empty row
        ['Equipment', 'Equipment Group', 'Date', 'Reason Category', 'Hours Lost', 'Remarks'],
        ...detailRows.map(row => [
          row.equipment,
          row.equipmentGroup,
          row.date,
          row.reason,
          formatMinutesToHHMM(row.minutes),
          row.remarks
        ])
      ];

      // Create workbook with 2 sheets
      const wb = XLSX.utils.book_new();
      
      // Sheet 1 - Pivot Summary
      const ws1 = XLSX.utils.aoa_to_sheet(sheet1Data);
      const colWidths1 = sheet1Data.reduce((widths, row) => {
        row.forEach((cell, idx) => {
          const cellLen = cell ? String(cell).length : 0;
          widths[idx] = Math.max(widths[idx] || 0, cellLen + 2);
        });
        return widths;
      }, []);
      ws1['!cols'] = colWidths1.map(w => ({ wch: Math.min(w, 50) }));
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');
      
      // Sheet 2 - Equipment Details
      const ws2 = XLSX.utils.aoa_to_sheet(sheet2Data);
      const colWidths2 = sheet2Data.reduce((widths, row) => {
        row.forEach((cell, idx) => {
          const cellLen = cell ? String(cell).length : 0;
          widths[idx] = Math.max(widths[idx] || 0, cellLen + 2);
        });
        return widths;
      }, []);
      ws2['!cols'] = colWidths2.map(w => ({ wch: Math.min(w, 50) }));
      XLSX.utils.book_append_sheet(wb, ws2, 'Equipment Details');

      // Download file
      XLSX.writeFile(wb, `reason_category_summary_${startDateFile}_to_${endDateFile}.xlsx`);
      message.success('Reason summary exported successfully (2 sheets)');
    } catch (error) {
      console.error('Error exporting reason summary:', error);
      message.error('Error exporting data: ' + error.message);
    }
  };

  // Time range quick select
  const handleTimeRangeChange = (range) => {
    setActiveTimeRange(range);
    setDateTimeRange(calculateDefaultRange(range));
  };

  // Update row data (for reasons and remarks) and track modifications
  const updateRowData = (key, field, value) => {
    setTableData(prev => prev.map(row => 
      row.key === key ? { ...row, [field]: value } : row
    ));
    // Track this row as modified
    setModifiedRows(prev => new Set([...prev, key]));
  };

  // Get count of modified rows
  const modifiedCount = modifiedRows.size;

  // Get modified rows data for saving
  const getModifiedRowsData = () => {
    return tableData.filter(row => modifiedRows.has(row.key) && row.planId);
  };

  // Save all modified rows
  const handleSaveChanges = async () => {
    const rowsToSave = getModifiedRowsData();
    
    if (rowsToSave.length === 0) {
      message.warning('No changes to save');
      return;
    }

    setSaving(true);
    setSaveModalVisible(false);
    
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const row of rowsToSave) {
      try {
        const reasons = Array.isArray(row.reason) ? row.reason : [];
        const durations = row.reasonDurations || {};
        const cycleVarMins = Math.abs(_parseVarianceToMinutes(row.cycleVariation));
        const filledMins = reasons.reduce((s, r) => s + (durations[r] != null ? durations[r] * 60 : 0), 0);
        const hasAnyFilled = reasons.some(r => durations[r] != null);
        const unaccountedHrs = (reasons.length >= 2 && hasAnyFilled && cycleVarMins > filledMins + 0.01)
          ? (cycleVarMins - filledMins) / 60
          : null;
        const reasonString = serializeReasonField(row.reason, row.reasonDurations, unaccountedHrs);
        await updateVarianceReason(row.planId, reasonString, row.remarks);
        successCount++
      } catch (error) {
        failCount++;
        errors.push(`${row.equipmentName} - ${row.batchOrId}: ${error.message}`);
      }
    }

    setSaving(false);

    if (failCount === 0) {
      message.success(`Successfully saved ${successCount} record(s)`);
      setModifiedRows(new Set()); // Clear modified tracking
    } else if (successCount > 0) {
      message.warning(`Saved ${successCount} record(s), ${failCount} failed`);
      // Remove successful saves from modified set
      const failedKeys = rowsToSave
        .filter((_, idx) => idx >= successCount)
        .map(r => r.key);
      setModifiedRows(new Set(failedKeys));
    } else {
      message.error(`Failed to save changes: ${errors[0]}`);
    }
  };

  // Show confirmation modal
  const showSaveConfirmation = () => {
    const rowsToSave = getModifiedRowsData();
    if (rowsToSave.length === 0) {
      message.warning('No changes to save. Only records with valid Plan ID can be saved.');
      return;
    }

    // Block if any duration is blank when ≥2 reasons, or if total exceeds cycle variance
    const durationErrors = [];
    rowsToSave.forEach(row => {
      const reasons = Array.isArray(row.reason) ? row.reason : [];
      if (reasons.length < 2) return;
      const durations = row.reasonDurations || {};
      const blankReasons = reasons.filter(r => durations[r] == null);
      if (blankReasons.length > 0) {
        durationErrors.push(`${row.equipmentName} — ${row.batchOrId}: Enter duration for all ${reasons.length} reasons (${blankReasons.length} still missing).`);
        return;
      }
      const totalMins = reasons.reduce((s, r) => s + durations[r] * 60, 0);
      const cycleVarMins = Math.abs(_parseVarianceToMinutes(row.cycleVariation));
      if (totalMins > cycleVarMins + 0.01) {
        const h = Math.floor(cycleVarMins / 60);
        const m = Math.round(cycleVarMins % 60);
        durationErrors.push(`${row.equipmentName} — ${row.batchOrId}: Total duration exceeds cycle variance (${h}:${m.toString().padStart(2, '0')} hrs).`);
      }
    });
    if (durationErrors.length > 0) {
      Modal.error({
        title: 'Fix duration errors before saving',
        content: (
          <ul style={{ paddingLeft: '16px', margin: '8px 0', fontSize: '12px' }}>
            {durationErrors.map((e, i) => <li key={i} style={{ marginBottom: '4px' }}>{e}</li>)}
          </ul>
        ),
        width: 520,
      });
      return;
    }

    setSaveModalVisible(true);
  };

  // Clear all modifications (discard changes)
  const handleDiscardChanges = () => {
    Modal.confirm({
      title: 'Discard Changes?',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to discard ${modifiedCount} unsaved change(s)? This will reload the data from server.`,
      okText: 'Yes, Discard',
      okType: 'danger',
      cancelText: 'No, Keep Changes',
      onOk: () => {
        fetchData();
        setModifiedRows(new Set());
      }
    });
  };

  useEffect(() => {
    if (dateTimeRange && dateTimeRange.length === 2) {
      fetchData();
      setModifiedRows(new Set()); // Clear modifications on new data fetch
    }
  }, [dateTimeRange, selectedPlanVersion]);

  // Fetch plan versions on mount
  useEffect(() => {
    const loadPlanVersions = async () => {
      setLoadingVersions(true);
      try {
        const response = await fetchPlanVersions();
        if (response?.success && response.data) {
          setPlanVersions(response.data);
        }
      } catch (error) {
        console.error('Error loading plan versions:', error);
      } finally {
        setLoadingVersions(false);
      }
    };
    loadPlanVersions();
  }, []);

  // Table columns for Variance Tracking
  const varianceColumns = [
    {
      title: 'Type',
      dataIndex: 'typeIcon',
      key: 'type',
      width: 50,
      align: 'center',
      fixed: 'left',
      render: (icon, record) => {
        if (record.isGroupHeader || record.isNoActivity) {
          return null; // Group header handled by Equipment column
        }
        return (
          <Tooltip title={record.type === 'batch' ? 'Batch Operation' : 'Cleaning Operation'}>
            <span style={{ fontSize: '14px' }}>{icon}</span>
          </Tooltip>
        );
      },
      onCell: (record) => {
        if (record.isGroupHeader || record.isNoActivity) {
          return { colSpan: 0 };
        }
        return {};
      }
    },
    {
      title: () => {
        const filterOptions = [
          { label: '🧪 Granulation', value: 'Granulation' },
          { label: '🌡️ FBP', value: 'FBP' },
          { label: '🔄 Blending', value: 'Blending' },
          { label: '⚙️ Compression', value: 'Compression' },
          { label: '🎨 Coating', value: 'Coating' },
          { label: '💊 Capsulation', value: 'Capsulation' },
          { label: '✍️ Imprinting', value: 'Imprinting' },
          { label: '📦 Packing', value: 'Packing' },
          { label: '📦 Other', value: 'Other' },
        ];
        const isFiltered = filteredGroups.length > 0;
        const popoverContent = (
          <div style={{ minWidth: 160 }}>
            {filterOptions.map(opt => (
              <div key={opt.value} style={{ padding: '4px 0' }}>
                <Checkbox
                  checked={filteredGroups.includes(opt.value)}
                  onChange={() => {
                    const next = filteredGroups.includes(opt.value)
                      ? filteredGroups.filter(g => g !== opt.value)
                      : [...filteredGroups, opt.value];
                    setFilteredGroups(next);
                  }}
                >
                  {opt.label}
                </Checkbox>
              </div>
            ))}
            <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 6, paddingTop: 6 }}>
              <span
                style={{ color: '#1677ff', fontSize: '12px', cursor: 'pointer' }}
                onClick={() => setFilteredGroups([])}
              >
                Reset
              </span>
            </div>
          </div>
        );
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
            Actual Loc
            <Popover
              content={popoverContent}
              trigger="click"
              placement="bottomLeft"
              getPopupContainer={() => document.body}
            >
              <FilterOutlined
                style={{
                  fontSize: '11px',
                  cursor: 'pointer',
                  color: isFiltered ? '#1677ff' : '#bfbfbf',
                  fontWeight: isFiltered ? 'bold' : 'normal',
                }}
              />
            </Popover>
          </span>
        );
      },
      dataIndex: 'equipmentName',
      key: 'equipment',
      width: 120,
      fixed: 'left',
      align: 'center',
      render: (value) => <span style={{ fontWeight: '600', fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}>{value}</span>,
    },
    {
      title: 'Plan Loc',
      dataIndex: 'planEquipmentLocation',
      key: 'planEquipmentLocation',
      width: 90,
      align: 'center',
      render: (value, record) => {
        if (record.isGroupHeader || record.isNoActivity) return null;
        return (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}>{value}</span>
        );
      },
      onCell: (record) => {
        if (record.isNoActivity) return { colSpan: 0 };
        return {};
      }
    },
    {
      title: 'Actual Loc',
      dataIndex: 'actualEquipmentLocation',
      key: 'actualEquipmentLocation',
      hidden: true,
      width: 200,
      align: 'center',
      render: (value, record) => {
        if (record.isGroupHeader || record.isNoActivity) return null;
        return (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}>{value}</span>
        );
      },
      onCell: (record) => {
        if (record.isNoActivity) return { colSpan: 0 };
        return {};
      }
    },
    {
      title: 'Batch/ID',
      dataIndex: 'batchOrId',
      key: 'batchOrId',
      width: 120,
      render: (value, record) => {
        if (record.isGroupHeader || record.isNoActivity) return null;
        return (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}>{value}</span>
        );
      },
      onCell: (record) => {
        if (record.isNoActivity) return { colSpan: 0 };
        return {};
      }
    },
    {
      title: (
        <span 
          style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
          onClick={() => setPlanColumnsExpanded(!planColumnsExpanded)}
        >
          Plan Dur 
          <span style={{ 
            fontSize: '10px', 
            fontWeight: 'bold',
            color: '#3182ce',
            transition: 'transform 0.2s',
            transform: planColumnsExpanded ? 'rotate(45deg)' : 'rotate(0deg)'
          }}>+</span>
        </span>
      ),
      dataIndex: 'planDuration',
      key: 'planDuration',
      width: 85,
      align: 'center',
      render: (value, record) => {
        if (record.isGroupHeader || record.isNoActivity) return null;
        return value;
      },
      onCell: (record) => {
        if (record.isNoActivity) return { colSpan: 0 };
        return {};
      }
    },
    ...(planColumnsExpanded ? [
      {
        title: 'Plan Start',
        dataIndex: 'planStart',
        key: 'planStart',
        width: 100,
        render: (value, record) => {
          if (record.isGroupHeader || record.isNoActivity) return null;
          return value;
        },
        onCell: (record) => {
          if (record.isNoActivity) return { colSpan: 0 };
          return {};
        }
      },
      {
        title: 'Plan End',
        dataIndex: 'planEnd',
        key: 'planEnd',
        width: 100,
        render: (value, record) => {
          if (record.isGroupHeader || record.isNoActivity) return null;
          return value;
        },
        onCell: (record) => {
          if (record.isNoActivity) return { colSpan: 0 };
          return {};
        }
      },
    ] : []),
    {
      title: (
        <span 
          style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
          onClick={() => setActualColumnsExpanded(!actualColumnsExpanded)}
        >
          Actual Dur 
          <span style={{ 
            fontSize: '10px', 
            fontWeight: 'bold',
            color: '#3182ce',
            transition: 'transform 0.2s',
            transform: actualColumnsExpanded ? 'rotate(45deg)' : 'rotate(0deg)'
          }}>+</span>
        </span>
      ),
      dataIndex: 'actualDuration',
      key: 'actualDuration',
      width: 90,
      align: 'center',
      render: (value, record) => {
        if (record.isGroupHeader || record.isNoActivity) return null;
        return value;
      },
      onCell: (record) => {
        if (record.isNoActivity) return { colSpan: 0 };
        return {};
      }
    },
    ...(actualColumnsExpanded ? [
      {
        title: 'Actual Start',
        dataIndex: 'actualStart',
        key: 'actualStart',
        width: 100,
        render: (value, record) => {
          if (record.isGroupHeader || record.isNoActivity) return null;
          return value;
        },
        onCell: (record) => {
          if (record.isNoActivity) return { colSpan: 0 };
          return {};
        }
      },
      {
        title: 'Actual End',
        dataIndex: 'actualEnd',
        key: 'actualEnd',
        width: 100,
        render: (value, record) => {
          if (record.isGroupHeader || record.isNoActivity) return null;
          return value;
        },
        onCell: (record) => {
          if (record.isNoActivity) return { colSpan: 0 };
          return {};
        }
      },
    ] : []),
    {
      title: 'Plan Var',
      dataIndex: 'planVariation',
      key: 'planVariation',
      hidden: true,
      width: 90,
      align: 'center',
      render: (value, record) => {
        if (record.isGroupHeader || record.isNoActivity) return null;
        if (value === '-' || value === 'running') return value;
        const isNegative = value.startsWith('-');
        const isZero = value === '+0:00' || value === '-0:00';
        return (
          <span style={{
            ...styles.badge,
            ...(isZero ? styles.badgeNeutral : isNegative ? styles.badgeSuccess : styles.badgeDanger)
          }}>
            {value}
          </span>
        );
      },
      onCell: (record) => {
        if (record.isNoActivity) return { colSpan: 0 };
        return {};
      }
    },
    {
      title: 'Cycle Var',
      dataIndex: 'cycleVariation',
      key: 'cycleVariation',
      width: 90,
      align: 'center',
      filterDropdown: ({ selectedKeys, setSelectedKeys, confirm }) => {
        const options = [
          { label: 'Positive (delays)', value: 'positive' },
          { label: 'Negative (early)', value: 'negative' },
        ];
        return (
          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
            {options.map(opt => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={selectedKeys.includes(opt.value)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...selectedKeys, opt.value]
                      : selectedKeys.filter(k => k !== opt.value);
                    setSelectedKeys(next);
                    confirm({ closeDropdown: false });
                  }}
                />
                {opt.label}
              </label>
            ))}
            {selectedKeys.length > 0 && (
              <button
                onClick={() => { setSelectedKeys([]); confirm({ closeDropdown: true }); }}
                style={{ marginTop: '4px', fontSize: '11px', cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: '4px', padding: '2px 8px', background: 'white', color: '#595959' }}
              >
                Reset
              </button>
            )}
          </div>
        );
      },
      onFilter: (value, record) => {
        if (record.isGroupHeader || record.isNoActivity) return true;
        const v = record.cycleVariation;
        if (!v || v === '-' || v === 'running') return false;
        const isZero = v === '+0:00' || v === '-0:00';
        if (isZero) return false;
        if (value === 'positive') return !v.startsWith('-');
        if (value === 'negative') return v.startsWith('-');
        return true;
      },
      render: (value, record) => {
        if (record.isGroupHeader || record.isNoActivity) return null;
        if (value === '-' || value === 'running') return value;
        const isNegative = value.startsWith('-');
        const isZero = value === '+0:00' || value === '-0:00';
        return (
          <span style={{
            ...styles.badge,
            ...(isZero ? styles.badgeNeutral : isNegative ? styles.badgeSuccess : styles.badgeDanger)
          }}>
            {value}
          </span>
        );
      },
      onCell: (record) => {
        if (record.isNoActivity) return { colSpan: 0 };
        return {};
      }
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      width: 260,
      render: (value, record) => {
        if (record.isGroupHeader || record.isNoActivity) return null;
        if (!record.cycleVariation || record.cycleVariation === '-' || record.cycleVariation === 'running') {
          return <span style={{ color: '#718096', opacity: 0.5 }}>-</span>;
        }
        const isModified = modifiedRows.has(record.key);
        const selectedReasons = Array.isArray(value) ? value : [];
        const reasonDurations = record.reasonDurations || {};
        const hasSomeDurations = selectedReasons.some(r => reasonDurations[r] != null);
        const isPanelOpen = expandedDurationRows.has(record.key);
        const showDurationInputs = selectedReasons.length >= 2 && isPanelOpen;

        // ── Calculations (only relevant when 2+ reasons) ──────────────────
        const cycleVarMinutes = Math.abs(_parseVarianceToMinutes(record.cycleVariation));
        const filledCount = selectedReasons.filter(r => reasonDurations[r] != null).length;
        const totalEnteredMins = selectedReasons.reduce((s, r) => s + (reasonDurations[r] != null ? reasonDurations[r] * 60 : 0), 0);
        const exceedsLimit = totalEnteredMins > cycleVarMinutes + 0.01;
        const allFilled = filledCount === selectedReasons.length;
        const unaccountedMins = allFilled && !exceedsLimit ? Math.max(0, cycleVarMinutes - totalEnteredMins) : 0;
        const fmtMins = (m) => { const h = Math.floor(m / 60); const mn = Math.round(m % 60); return `${h.toString().padStart(2, '0')}:${mn.toString().padStart(2, '0')}`; };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {/* Multi-select row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {isModified && (
                <Tooltip title="Unsaved changes">
                  <Badge status="processing" />
                </Tooltip>
              )}
              <Select
                size="small"
                mode="multiple"
                value={selectedReasons.length > 0 ? selectedReasons : undefined}
                placeholder="Select reason(s)"
                onChange={(val) => {
                  const newReasons = val || [];
                  const updatedDurations = {};
                  newReasons.forEach(r => { if (reasonDurations[r] != null) updatedDurations[r] = reasonDurations[r]; });
                  updateRowData(record.key, 'reason', newReasons);
                  updateRowData(record.key, 'reasonDurations', updatedDurations);
                  setExpandedDurationRows(prev => {
                    const next = new Set(prev);
                    if (newReasons.length >= 2) next.add(record.key);
                    else next.delete(record.key);
                    return next;
                  });
                }}
                allowClear
                maxTagCount={1}
                maxTagTextLength={12}
                maxTagPlaceholder={(omittedValues) => (
                  <span style={{ color: '#1890ff', fontWeight: '600', fontSize: '10px' }}>
                    +{omittedValues.length} more
                  </span>
                )}
                style={{
                  width: '100%',
                  fontSize: '11px',
                  ...(isModified ? { borderColor: '#1890ff' } : {})
                }}
                dropdownStyle={{ fontSize: '11px', minWidth: '200px' }}
              >
                {REASON_OPTIONS.map((reason) => (
                  <Select.Option key={reason} value={reason}>{reason}</Select.Option>
                ))}
              </Select>
              {selectedReasons.length >= 2 && (
                <Tooltip title={isPanelOpen ? 'Hide durations' : hasSomeDurations ? 'View / edit durations' : 'Add durations'}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedDurationRows(prev => {
                        const next = new Set(prev);
                        if (next.has(record.key)) next.delete(record.key);
                        else next.add(record.key);
                        return next;
                      });
                    }}
                    style={{
                      width: '22px', height: '22px', flexShrink: 0,
                      border: `1px solid ${isPanelOpen ? '#1890ff' : hasSomeDurations ? '#52c41a' : '#d9d9d9'}`,
                      borderRadius: '4px',
                      background: isPanelOpen ? '#e6f7ff' : hasSomeDurations ? '#f6ffed' : 'white',
                      color: isPanelOpen ? '#1890ff' : hasSomeDurations ? '#52c41a' : '#8c8c8c',
                      cursor: 'pointer', fontSize: '15px', fontWeight: '700',
                      lineHeight: 1, padding: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {isPanelOpen ? '−' : '+'}
                  </button>
                </Tooltip>
              )}
            </div>

            {/* Inline duration inputs — only when 2+ reasons selected */}
            {showDurationInputs && (
              <div style={{
                background: '#f7fafc',
                border: `1px solid ${exceedsLimit ? '#ffccc7' : '#e2e8f0'}`,
                borderRadius: '4px',
                padding: '5px 7px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}>
                {selectedReasons.map(reason => (
                  <div key={reason} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{
                      fontSize: '10px', color: '#4a5568', flex: 1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {reason}
                    </span>
                    <DurationInput
                      value={reasonDurations[reason] != null ? reasonDurations[reason] : null}
                      hasError={exceedsLimit && reasonDurations[reason] != null}
                      onChange={(val) => {
                        const updated = { ...reasonDurations, [reason]: val };
                        updateRowData(record.key, 'reasonDurations', updated);
                      }}
                    />
                  </div>
                ))}

                {/* Divider + footer */}
                <div style={{ borderTop: '1px solid #e2e8f0', marginTop: '2px', paddingTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {exceedsLimit && (
                    <div style={{ fontSize: '10px', color: '#ff4d4f', fontWeight: '600' }}>
                      ⚠ Total {fmtMins(totalEnteredMins)} exceeds cycle var {fmtMins(cycleVarMinutes)}
                    </div>
                  )}
                  {!exceedsLimit && allFilled && unaccountedMins > 0.5 && (
                    <div style={{ fontSize: '10px', color: '#b7791f', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Unaccounted</span>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: '600' }}>
                        {fmtMins(unaccountedMins)} hrs
                      </span>
                    </div>
                  )}
                  {!exceedsLimit && allFilled && unaccountedMins <= 0.5 && (
                    <div style={{ fontSize: '10px', color: '#38a169', fontWeight: '600' }}>
                      ✓ Fully accounted
                    </div>
                  )}
                  {!exceedsLimit && !allFilled && filledCount > 0 && (
                    <div style={{ fontSize: '10px', color: '#d97706', fontWeight: '500' }}>
                      ⚠ {selectedReasons.length - filledCount} of {selectedReasons.length} durations missing
                    </div>
                  )}
                  {filledCount === 0 && (
                    <div style={{ fontSize: '10px', color: '#a0aec0' }}>Enter HH:MM for each reason (required)</div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      },
      onCell: (record) => {
        if (record.isNoActivity) return { colSpan: 0 };
        return {};
      }
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 200,
      render: (value, record) => {
        if (record.isGroupHeader || record.isNoActivity) return null;
        // Show remarks field only when cycle variance exists (positive or negative)
        if (!record.cycleVariation || record.cycleVariation === '-' || record.cycleVariation === 'running') {
          return <span style={{ color: '#718096', opacity: 0.5 }}>-</span>;
        }
        const isModified = modifiedRows.has(record.key);
        return (
          <Input
            size="small"
            value={value}
            placeholder="Enter remarks..."
            onChange={(e) => updateRowData(record.key, 'remarks', e.target.value)}
            style={{ 
              fontSize: '11px', 
              width: '170px',
              ...(isModified ? { borderColor: '#1890ff', backgroundColor: '#f0f7ff' } : {})
            }}
          />
        );
      },
      onCell: (record) => {
        if (record.isNoActivity) return { colSpan: 0 };
        return {};
      }
    }
  ];

  const dateRangeDisplay = dateTimeRange && dateTimeRange.length === 2
    ? `${dateTimeRange[0].format('MMM DD')} - ${dateTimeRange[1].format('MMM DD, YYYY')}`
    : 'Select Date Range';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Production Variance Dashboard</h1>
        <div style={styles.headerMeta}>
          Last Updated: <strong>{dayjs().format('MMM DD, YYYY HH:mm')}</strong>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabNav}>
        <button
          style={{
            ...styles.tabBtn,
            ...(activeTab === 'variance' ? styles.tabBtnActive : {})
          }}
          onClick={() => setActiveTab('variance')}
        >
          Variance Tracking
        </button>
        <button
          style={{
            ...styles.tabBtn,
            ...(activeTab === 'reasons' ? styles.tabBtnActive : {})
          }}
          onClick={() => setActiveTab('reasons')}
        >
          Reasons Summary
        </button>
      </div>

      {/* Variance Tracking Tab */}
      {activeTab === 'variance' && (
        <>
          {/* Filters */}
          <div style={styles.filters}>
            {/* Operation Slicers */}
            <div style={styles.slicers}>
              {['all', 'batch', 'cleaning'].map((op) => (
                <div
                  key={op}
                  style={{
                    ...styles.slicerItem,
                    ...(operationFilter === op ? styles.slicerItemActive : {}),
                    ...(op === 'cleaning' ? { borderRight: 'none' } : {})
                  }}
                  onClick={() => setOperationFilter(op)}
                >
                  {op === 'all' ? 'All' : op === 'batch' ? 'Batch' : 'Cleaning'}
                </div>
              ))}
            </div>

            <div style={styles.filterDivider} />

            {/* Date Time Range Picker */}
            <div style={styles.datetimeGroup}>
              <RangePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                value={dateTimeRange}
                onChange={(dates) => {
                  setDateTimeRange(dates);
                  setActiveTimeRange(null);
                }}
                size="small"
                style={{ minWidth: '300px' }}
              />
            </div>

            <div style={styles.filterDivider} />

            {/* Quick Time Range */}
            <div style={styles.timeSlicers}>
              {['1D', '1W', '1M', '3M', '6M'].map((range) => (
                <button
                  key={range}
                  style={{
                    ...styles.timeBtn,
                    ...(activeTimeRange === range ? styles.timeBtnActive : {})
                  }}
                  onClick={() => handleTimeRangeChange(range)}
                >
                  {range}
                </button>
              ))}
            </div>

            <div style={styles.filterDivider} />

            {/* Plan Version */}
            <Select
              value={selectedPlanVersion}
              onChange={(value) => setSelectedPlanVersion(value)}
              loading={loadingVersions}
              size="small"
              style={{ minWidth: '150px' }}
              placeholder="Plan Version"
            >
              <Select.Option value={null}>Latest Version</Select.Option>
              {planVersions.map((v) => (
                <Select.Option key={v.plan_version} value={v.plan_version}>
                  v{v.plan_version} - {dayjs(v.published_at).format('DD MMM HH:mm')}
                </Select.Option>
              ))}
            </Select>
          </div>


          {/* Data Table */}
          <div style={styles.tableContainer}>
            <div style={styles.tableHeader}>
              <h2 style={styles.tableTitle}>
                Variance Records
                {modifiedRows.size > 0 && (
                  <Badge 
                    count={modifiedRows.size} 
                    style={{ backgroundColor: '#fa8c16', marginLeft: '8px' }}
                    title={`${modifiedRows.size} unsaved changes`}
                  />
                )}
              </h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                {modifiedRows.size > 0 && (
                  <>
                    <Button 
                      size="small" 
                      onClick={handleDiscardChanges}
                      style={{ color: '#8c8c8c' }}
                    >
                      Discard
                    </Button>
                    <Button 
                      type="primary"
                      size="small" 
                      icon={<SaveOutlined />} 
                      onClick={showSaveConfirmation}
                      style={{ background: '#52c41a', borderColor: '#52c41a' }}
                    >
                      Save Changes ({modifiedRows.size})
                    </Button>
                  </>
                )}
                <Button 
                  size="small" 
                  icon={<ReloadOutlined />} 
                  onClick={fetchData} 
                  loading={loading}
                >
                  Refresh
                </Button>
                <Button 
                  type="primary"
                  size="small" 
                  icon={<DownloadOutlined />} 
                  onClick={handleExportExcel} 
                  loading={exporting}
                  style={{ background: '#2c5282' }}
                >
                  Export to Excel
                </Button>
              </div>
            </div>
            <Spin spinning={loading}>
              <Table
                columns={varianceColumns}
                dataSource={groupedTableData}
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  pageSizeOptions: ['25', '50', '100', '200'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                }}
                scroll={{ x: 1400, y: 450 }}
                sticky={{ offsetHeader: 0 }}
                size="small"
                rowClassName={(record) => record.type === 'batch' ? 'batch-row' : 'cleaning-row'}
                locale={{ emptyText: 'No activities found for the selected date range' }}
                style={{ fontSize: '11px' }}
              />
            </Spin>
          </div>
        </>
      )}

      {/* Reasons Summary Tab */}
      {activeTab === 'reasons' && (
        <>
          {/* Filters - Same as Variance Tracking */}
          <div style={styles.filters}>
            <div style={styles.slicers}>
              {['all', 'batch', 'cleaning'].map((op) => (
                <div
                  key={op}
                  style={{
                    ...styles.slicerItem,
                    ...(operationFilter === op ? styles.slicerItemActive : {}),
                    ...(op === 'cleaning' ? { borderRight: 'none' } : {})
                  }}
                  onClick={() => setOperationFilter(op)}
                >
                  {op === 'all' ? 'All' : op === 'batch' ? 'Batch' : 'Cleaning'}
                </div>
              ))}
            </div>
            <div style={styles.filterDivider} />
            <div style={styles.datetimeGroup}>
              <RangePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                value={dateTimeRange}
                onChange={(dates) => {
                  setDateTimeRange(dates);
                  setActiveTimeRange(null);
                }}
                size="small"
                style={{ minWidth: '300px' }}
              />
            </div>
            <div style={styles.filterDivider} />
            <div style={styles.timeSlicers}>
              {['1D', '1W', '1M', '3M', '6M'].map((range) => (
                <button
                  key={range}
                  style={{
                    ...styles.timeBtn,
                    ...(activeTimeRange === range ? styles.timeBtnActive : {})
                  }}
                  onClick={() => handleTimeRangeChange(range)}
                >
                  {range}
                </button>
              ))}
            </div>
            <div style={styles.filterDivider} />
            <Select
              mode="multiple"
              placeholder="All Equipment Groups"
              value={reasonsEquipmentFilter}
              onChange={(vals) => setReasonsEquipmentFilter(vals)}
              style={{ minWidth: '180px' }}
              size="small"
              allowClear
              maxTagCount={2}
              options={[
                { label: '🧪 Granulation', value: 'Granulation' },
                { label: '🌡️ FBP', value: 'FBP' },
                { label: '🔄 Blending', value: 'Blending' },
                { label: '⚙️ Compression', value: 'Compression' },
                { label: '🎨 Coating', value: 'Coating' },
                { label: '💊 Capsulation', value: 'Capsulation' },
                { label: '✍️ Imprinting', value: 'Imprinting' },
                { label: '📦 Packing', value: 'Packing' },
                { label: '📦 Other', value: 'Other' }
              ]}
            />
            <Select
              mode="multiple"
              placeholder="All Equipment"
              value={reasonsEquipmentNumbers}
              onChange={(vals) => setReasonsEquipmentNumbers(vals)}
              style={{ minWidth: '180px' }}
              size="small"
              allowClear
              maxTagCount={2}
              options={availableEquipmentOptions.filter(eq => 
                reasonsEquipmentFilter.length === 0 || reasonsEquipmentFilter.includes(eq.area)
              )}
              disabled={availableEquipmentOptions.length === 0}
            />
          </div>

          {/* Chart + Table wrapped in Spin for filter feedback */}
          <Spin spinning={reasonsLoading || loading} tip="Updating..." size="large">

          {/* Chart Container */}
          <div style={{ 
            background: 'white', 
            borderRadius: '8px', 
            padding: '20px', 
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e2e8f0',
            marginBottom: '10px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#1a202c', margin: 0 }}>
                  Hours Lost by Reason Category
                </h2>
                <Tooltip title="This dashboard shows only positive cycle time variances (delays) - where actual duration exceeded planned duration. Negative variances (early completions) are excluded.">
                  <InfoCircleOutlined style={{ color: '#718096', fontSize: '14px', cursor: 'help' }} />
                </Tooltip>
              </div>
              <span style={{ fontSize: '12px', color: '#718096', fontFamily: "'JetBrains Mono', monospace" }}>
                {dateRangeDisplay}
              </span>
            </div>

            {/* Simple Bar Chart using divs */}
            {(() => {
              // Helper to parse variance string to minutes for HH:MM format
              const parseVarianceToMinutes = (variance) => {
                if (!variance || variance === '-' || variance === 'running') return 0;
                const match = variance.match(/^([+-])?(\d+):(\d+)$/);
                if (!match) return 0;
                const sign = match[1] === '-' ? -1 : 1;
                const hours = parseInt(match[2], 10);
                const minutes = parseInt(match[3], 10);
                return sign * (hours * 60 + minutes);
              };

              // Helper to format minutes as HH:MM
              const formatMinutesToHHMM = (totalMinutes) => {
                const hours = Math.floor(totalMinutes / 60);
                const mins = Math.round(totalMinutes % 60);
                return `${hours}:${mins.toString().padStart(2, '0')}`;
              };

              // Filter data by equipment for Reasons Summary
              const dataForReasons = reasonsEquipmentNumbers.length > 0
                ? filteredTableData.filter(row => reasonsEquipmentNumbers.includes(row.equipmentName))
                : (reasonsEquipmentFilter.length > 0
                  ? filteredTableData.filter(row => reasonsEquipmentFilter.includes(row.area))
                  : filteredTableData);

              // Calculate minutes lost by reason (ONLY positive cycle variance = delays)
              const reasonMinutes = {};
              REASON_OPTIONS.forEach(r => reasonMinutes[r] = 0);
              let unaccountedTotal = 0;

              dataForReasons.forEach(row => {
                const minuteMap = getMinutesPerReasonMap(row);
                Object.entries(minuteMap).forEach(([reason, mins]) => {
                  if (reasonMinutes.hasOwnProperty(reason)) {
                    reasonMinutes[reason] += mins;
                  }
                });
                unaccountedTotal += getUnaccountedMinutes(row);
              });

              // Calculate total minutes for percentage
              const totalMinutes = Object.values(reasonMinutes).reduce((sum, m) => sum + m, 0) + unaccountedTotal;

              const chartData = REASON_OPTIONS
                .map(reason => ({ 
                  reason, 
                  minutes: reasonMinutes[reason],
                  percentage: totalMinutes > 0 ? Math.round((reasonMinutes[reason] / totalMinutes) * 100) : 0
                }))
                .filter(d => d.minutes > 0)
                .sort((a, b) => b.percentage - a.percentage);

              if (unaccountedTotal > 0.5) {
                chartData.push({
                  reason: 'Unaccounted',
                  minutes: unaccountedTotal,
                  percentage: totalMinutes > 0 ? Math.round((unaccountedTotal / totalMinutes) * 100) : 0,
                  isUnaccounted: true
                });
              }

              const maxMinutes = Math.max(...chartData.map(d => d.minutes), 1);

              if (chartData.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                    <InfoCircleOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                    <div>No positive cycle variance data with reasons assigned yet.</div>
                    <div style={{ fontSize: '11px', marginTop: '4px' }}>Assign reasons to delays in the Variance Tracking tab to see the chart.</div>
                  </div>
                );
              }

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {chartData.map((item, idx) => (
                    <div key={item.reason} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ 
                        width: '180px', 
                        fontSize: '11px', 
                        color: item.isUnaccounted ? '#b7791f' : '#4a5568',
                        fontStyle: item.isUnaccounted ? 'italic' : 'normal',
                        textAlign: 'right',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {item.reason}
                      </div>
                      <div style={{ flex: 1, height: '24px', background: '#f7fafc', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${(item.minutes / maxMinutes) * 100}%`,
                          height: '100%',
                          background: item.isUnaccounted ? '#d97706' : `hsl(${220 - idx * 20}, 70%, ${55 + idx * 3}%)`,
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          paddingRight: '8px',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: '600',
                          minWidth: '80px'
                        }}>
                          {formatMinutesToHHMM(item.minutes)} ({item.percentage}%)
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Reasons Summary Table */}
          <div style={{ 
            background: 'white', 
            borderRadius: '8px', 
            padding: '20px', 
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e2e8f0',
            marginBottom: '10px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ fontSize: '14px', fontWeight: '700', color: '#1a202c', margin: 0 }}>
                  Reason Category Summary
                </h2>
                <Tooltip title="Shows only positive cycle time variances (delays) - where actual duration exceeded planned duration">
                  <InfoCircleOutlined style={{ color: '#718096', fontSize: '14px', cursor: 'help' }} />
                </Tooltip>
              </div>
              <Button size="small" icon={<DownloadOutlined />} style={{ background: '#2c5282', color: 'white' }} onClick={handleExportReasonsSummary}>
                Export to Excel
              </Button>
            </div>

            {/* Reason Summary Table - Pivot Format */}
            <Table
              size="small"
              pagination={false}
              style={{ fontSize: '12px' }}
              scroll={{ x: 'max-content' }}
              expandable={{
                expandedRowRender: (record) => {
                  if (record.isTotal || !record.equipmentBreakdown || record.equipmentBreakdown.length === 0) {
                    return null;
                  }
                  return (
                    <div style={{ padding: '8px 16px', background: '#fafafa' }}>
                      <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f0f0f0' }}>
                            <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: '600' }}>Equipment</th>
                            <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: '600', width: '120px' }}>Equipment Group</th>
                            <th style={{ padding: '6px 12px', textAlign: 'center', fontWeight: '600', width: '120px' }}>Hours Lost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {record.equipmentBreakdown.map((item, idx) => {
                            const hours = Math.floor(item.minutes / 60);
                            const mins = Math.round(item.minutes % 60);
                            return (
                              <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '6px 12px', color: '#4a5568', fontWeight: '500' }}>
                                  {item.equipmentName}
                                </td>
                                <td style={{ padding: '6px 12px', color: '#718096' }}>
                                  {item.area}
                                </td>
                                <td style={{ 
                                  padding: '6px 12px', 
                                  textAlign: 'center',
                                  fontFamily: "'JetBrains Mono', monospace",
                                  color: '#e53e3e'
                                }}>
                                  {hours}:{mins.toString().padStart(2, '0')}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                },
                rowExpandable: (record) => !record.isTotal && record.equipmentBreakdown && record.equipmentBreakdown.length > 0,
              }}
              dataSource={(() => {
                // Helper to parse variance string to minutes
                const parseVarianceToMinutes = (variance) => {
                  if (!variance || variance === '-' || variance === 'running') return 0;
                  const match = variance.match(/^([+-])?(\d+):(\d+)$/);
                  if (!match) return 0;
                  const sign = match[1] === '-' ? -1 : 1;
                  const hours = parseInt(match[2], 10);
                  const minutes = parseInt(match[3], 10);
                  return sign * (hours * 60 + minutes);
                };

                // Filter data by equipment for Reasons Summary
                const dataForReasons = reasonsEquipmentNumbers.length > 0
                  ? filteredTableData.filter(row => reasonsEquipmentNumbers.includes(row.equipmentName))
                  : (reasonsEquipmentFilter.length > 0
                    ? filteredTableData.filter(row => reasonsEquipmentFilter.includes(row.area))
                    : filteredTableData);

                // Build pivot data structure: { reason: { equipmentGroup: minutes, equipments: { equipmentName: minutes } } }
                const UNACCOUNTED_KEY = 'Unaccounted';
                const pivotData = {};
                [...REASON_OPTIONS, UNACCOUNTED_KEY].forEach(r => {
                  pivotData[r] = {
                    byGroup: {},
                    byEquipment: {}
                  };
                  EQUIPMENT_GROUPS.forEach(g => pivotData[r].byGroup[g] = 0);
                });
                
                dataForReasons.forEach(row => {
                  const minuteMap = getMinutesPerReasonMap(row);
                  const reasons = Object.keys(minuteMap);
                  const equipmentGroup = row.area || 'Other';
                  const equipmentName = row.equipmentName || 'Unknown';
                  if (reasons.length > 0) {
                    reasons.forEach(reason => {
                      if (pivotData[reason]) {
                        pivotData[reason].byGroup[equipmentGroup] = (pivotData[reason].byGroup[equipmentGroup] || 0) + minuteMap[reason];
                        if (!pivotData[reason].byEquipment[equipmentName]) {
                          pivotData[reason].byEquipment[equipmentName] = { minutes: 0, area: equipmentGroup };
                        }
                        pivotData[reason].byEquipment[equipmentName].minutes += minuteMap[reason];
                      }
                    });
                  }
                  const uMins = getUnaccountedMinutes(row);
                  if (uMins > 0.5) {
                    pivotData[UNACCOUNTED_KEY].byGroup[equipmentGroup] = (pivotData[UNACCOUNTED_KEY].byGroup[equipmentGroup] || 0) + uMins;
                    if (!pivotData[UNACCOUNTED_KEY].byEquipment[equipmentName]) {
                      pivotData[UNACCOUNTED_KEY].byEquipment[equipmentName] = { minutes: 0, area: equipmentGroup };
                    }
                    pivotData[UNACCOUNTED_KEY].byEquipment[equipmentName].minutes += uMins;
                  }
                });

                // Calculate column totals for each equipment group
                const columnTotals = {};
                EQUIPMENT_GROUPS.forEach(g => columnTotals[g] = 0);
                
                // Build data rows
                const dataRows = [...REASON_OPTIONS, UNACCOUNTED_KEY].map((reason, idx) => {
                  const isUnaccounted = reason === UNACCOUNTED_KEY;
                  const rowTotal = Object.values(pivotData[reason].byGroup).reduce((sum, m) => sum + m, 0);
                  
                  // Add to column totals
                  EQUIPMENT_GROUPS.forEach(g => {
                    columnTotals[g] += pivotData[reason].byGroup[g];
                  });
                  
                  // Build equipment breakdown array sorted by minutes descending
                  const equipmentBreakdown = Object.entries(pivotData[reason].byEquipment)
                    .map(([equipmentName, data]) => ({
                      equipmentName,
                      area: data.area,
                      minutes: data.minutes
                    }))
                    .sort((a, b) => b.minutes - a.minutes);
                  
                  return {
                    key: idx,
                    reason,
                    ...pivotData[reason].byGroup,
                    total: rowTotal,
                    equipmentBreakdown,
                    isUnaccounted
                  };
                }).filter(r => r.total > 0);

                // Add total row
                const grandTotal = Object.values(columnTotals).reduce((sum, m) => sum + m, 0);
                
                if (dataRows.length > 0) {
                  dataRows.push({
                    key: 'total',
                    reason: 'TOTAL',
                    ...columnTotals,
                    total: grandTotal,
                    isTotal: true
                  });
                }

                return dataRows;
              })()}
              columns={[
                {
                  title: 'Reason Category',
                  dataIndex: 'reason',
                  key: 'reason',
                  fixed: 'left',
                  width: 180,
                  render: (val, record) => (
                    <strong style={{
                      color: record.isTotal ? '#1a202c' : record.isUnaccounted ? '#b7791f' : '#4a5568',
                      fontStyle: record.isUnaccounted ? 'italic' : 'normal'
                    }}>{val}</strong>
                  )
                },
                ...EQUIPMENT_GROUPS.map(group => ({
                  title: group,
                  dataIndex: group,
                  key: group,
                  align: 'center',
                  width: 90,
                  render: (val, record) => {
                    if (!val || val === 0) return <span style={{ color: '#cbd5e0' }}>-</span>;
                    const hours = Math.floor(val / 60);
                    const mins = Math.round(val % 60);
                    return (
                      <span style={{ 
                        color: '#e53e3e', 
                        fontWeight: record.isTotal ? '700' : '500',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '11px'
                      }}>
                        {hours}:{mins.toString().padStart(2, '0')}
                      </span>
                    );
                  }
                })),
                {
                  title: 'Total',
                  dataIndex: 'total',
                  key: 'total',
                  align: 'center',
                  fixed: 'right',
                  width: 90,
                  render: (val, record) => {
                    const hours = Math.floor(val / 60);
                    const mins = Math.round(val % 60);
                    return (
                      <span style={{ 
                        color: '#1a202c', 
                        fontWeight: '700',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '11px'
                      }}>
                        {hours}:{mins.toString().padStart(2, '0')}
                      </span>
                    );
                  }
                }
              ]}
              rowClassName={(record) => record.isTotal ? 'total-row' : ''}
              locale={{ emptyText: 'No delays with reasons found. Only positive cycle variances (delays) with assigned reasons are shown here.' }}
            />
          </div>

          {/* Info message */}
          <div style={{
            background: '#ebf8ff',
            border: '1px solid #90cdf4',
            borderRadius: '8px',
            padding: '12px 16px',
            color: '#2c5282',
            fontSize: '12px'
          }}>
            <InfoCircleOutlined style={{ marginRight: '8px' }} />
            <strong>Tip:</strong> Assign reasons to variance records in the "Variance Tracking" tab to see the summary here.
            The chart visualization will populate as more reasons are assigned.
          </div>

          </Spin>{/* end reasonsLoading Spin */}
        </>
      )}

      {/* Save Confirmation Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ExclamationCircleOutlined style={{ color: '#fa8c16', fontSize: '20px' }} />
            <span>Confirm Save Changes</span>
          </div>
        }
        open={saveModalVisible}
        onOk={handleSaveChanges}
        onCancel={() => setSaveModalVisible(false)}
        okText={saving ? 'Saving...' : `Save ${modifiedRows.size} Record(s)`}
        cancelText="Cancel"
        okButtonProps={{ 
          loading: saving, 
          style: { background: '#52c41a', borderColor: '#52c41a' },
          disabled: tableData
            .filter(row => modifiedRows.has(row.key))
            .some(row => {
              const hasReasons = Array.isArray(row.reason) ? row.reason.length > 0 : !!row.reason;
              return hasReasons && !row.remarks?.trim();
            })
        }}
        width={700}
        centered
      >
        <div style={{ padding: '16px 0' }}>
          {/* Warning for missing remarks */}
          {tableData
            .filter(row => modifiedRows.has(row.key))
            .some(row => {
              const hasReasons = Array.isArray(row.reason) ? row.reason.length > 0 : !!row.reason;
              return hasReasons && !row.remarks?.trim();
            }) && (
            <div style={{
              background: '#fff2e8',
              border: '1px solid #ffbb96',
              borderRadius: '6px',
              padding: '10px 16px',
              marginBottom: '16px',
              color: '#d4380d',
              fontSize: '12px'
            }}>
              <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
              <strong>Remarks Required:</strong> Please fill in remarks for all records with reasons assigned. 
              Records with missing remarks are highlighted in red below.
            </div>
          )}
          
          <p style={{ marginBottom: '16px', color: '#595959' }}>
            You are about to save changes to <strong>{modifiedRows.size}</strong> record(s). 
            Please review before confirming:
          </p>
          <div style={{ 
            maxHeight: '350px', 
            overflowY: 'auto', 
            border: '1px solid #f0f0f0', 
            borderRadius: '6px' 
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#fafafa', position: 'sticky', top: 0 }}>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', width: '22%' }}>Equipment</th>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', width: '20%' }}>Batch / ID</th>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', width: '35%' }}>Reason &amp; Duration</th>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', width: '23%' }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {tableData
                  .filter(row => modifiedRows.has(row.key))
                  .map(row => {
                    const reasons = Array.isArray(row.reason) ? row.reason : (row.reason ? [row.reason] : []);
                    const durations = row.reasonDurations || {};
                    const hasReasons = reasons.length > 0;
                    const missingRemarks = hasReasons && !row.remarks?.trim();
                    const fmtMins = (m) => { const h = Math.floor(m / 60); const mn = Math.round(m % 60); return `${h}:${mn.toString().padStart(2, '0')}`; };

                    return (
                      <tr
                        key={row.key}
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          background: missingRemarks ? '#fff1f0' : 'transparent'
                        }}
                      >
                        <td style={{ padding: '10px', verticalAlign: 'top' }}>
                          <div style={{ fontWeight: 600, fontSize: '12px', color: '#262626' }}>{row.equipmentName}</div>
                          <div style={{ fontSize: '10px', color: '#8c8c8c', marginTop: '2px' }}>{row.type === 'batch' ? 'Batch' : 'Cleaning'}</div>
                        </td>
                        <td style={{ padding: '10px', verticalAlign: 'top' }}>
                          <div style={{ fontWeight: 500, fontSize: '12px', color: '#262626', fontFamily: "'JetBrains Mono', monospace" }}>{row.batchOrId}</div>
                          {row.cycleVariation && row.cycleVariation !== '-' && row.cycleVariation !== 'running' && (
                            <div style={{ fontSize: '10px', color: row.cycleVariation.startsWith('-') ? '#38a169' : '#e53e3e', marginTop: '2px' }}>
                              CV: {row.cycleVariation}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '10px', verticalAlign: 'top' }}>
                          {hasReasons ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                              {reasons.map(r => (
                                <div key={r} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '11px', color: '#262626' }}>{r}</span>
                                  {durations[r] != null ? (
                                    <span style={{ fontSize: '10px', fontFamily: "'JetBrains Mono', monospace", color: '#2c5282', background: '#ebf8ff', borderRadius: '3px', padding: '1px 5px', whiteSpace: 'nowrap' }}>
                                      {fmtMins(durations[r] * 60)} hrs
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: '10px', color: '#a0aec0' }}>—</span>
                                  )}
                                </div>
                              ))}
                              {(() => { const uMins = getUnaccountedMinutes(row); return uMins > 0.5 ? (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', borderTop: '1px dashed #e2e8f0', paddingTop: '3px', marginTop: '1px' }}>
                                  <span style={{ fontSize: '11px', color: '#b7791f', fontStyle: 'italic' }}>Unaccounted</span>
                                  <span style={{ fontSize: '10px', fontFamily: "'JetBrains Mono', monospace", color: '#b7791f', background: '#fffbeb', borderRadius: '3px', padding: '1px 5px', whiteSpace: 'nowrap' }}>
                                    {fmtMins(uMins)} hrs
                                  </span>
                                </div>
                              ) : null; })()}
                            </div>
                          ) : (
                            <span style={{ color: '#bfbfbf', fontSize: '11px' }}>Not selected</span>
                          )}
                        </td>
                        <td style={{ padding: '8px' }}>
                          <Input
                            size="small"
                            value={row.remarks || ''}
                            placeholder={missingRemarks ? 'Required - Enter remarks...' : 'Enter remarks...'}
                            onChange={(e) => updateRowData(row.key, 'remarks', e.target.value)}
                            style={{ 
                              fontSize: '11px',
                              borderColor: missingRemarks ? '#ff4d4f' : undefined,
                              background: missingRemarks ? '#fff1f0' : undefined
                            }}
                            status={missingRemarks ? 'error' : undefined}
                          />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <p style={{ marginTop: '16px', color: '#8c8c8c', fontSize: '11px' }}>
            <InfoCircleOutlined style={{ marginRight: '4px' }} />
            This action will update the database. Changes cannot be automatically undone.
          </p>
        </div>
      </Modal>

      {/* Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap');
        
        .batch-row { background: #ffffff; }
        .batch-row:hover { background: #f1f5f9 !important; }
        .cleaning-row { background: #f0f9ff; }
        .cleaning-row:hover { background: #e0f2fe !important; }
        .total-row { background: #f7fafc !important; font-weight: 700; }
        .total-row td { border-top: 2px solid #e2e8f0 !important; }
        
        .ant-table-thead > tr > th {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
          background: #fafafa !important;
          font-size: 10px !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.3px !important;
          padding: 7px 9px !important;
        }
        
        .ant-table-tbody > tr > td {
          padding: 7px 9px !important;
          font-size: 11px !important;
        }
        
        .ant-table-header {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
        }
      `}</style>
    </div>
  );
};

export default ProductionVarianceDashboard;
